import { nanoid } from '@reduxjs/toolkit';
import uuid from "react-uuid";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/pl';
import { getCurrentDate } from 'utility/currentDate';

import { setSellerValidationObjectValuesTo, setBuyerValidationObjectValuesTo } from './invoiceEditorValidationHelper';

const DefaultSeriesPrefix = 'F';

const toItemDataGridRow = (data) => {
    return {
        grossValue: data.amountGross,
        id: nanoid(),
        productId: data.productId,
        name: data.name,
        pkwiu: data.pkWiU,
        netValue: data.amountNet,
        quantity: data.quantity,
        unit: data.unit,
        unitNetPrice: data.unitNetPrice,
        vat: {
            name: data.vat.name,
            value: data.vat.value
        }
    };
};

export const defaultInvoiceData = () => {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    var now = dayjs();

    return {
        id: nanoid(),
        guid: uuid(),
        series: DefaultSeriesPrefix + '/' + now.format('YYYY'),
        seriesPrefix: DefaultSeriesPrefix,
        numberPromise: 0,
        number: 0,
        dateOfIssueUtc: now,
        dateOfSaleUtc: now,
        seller: {
            name: '',
            nip: {
                nip: ''
            },
            address: {
                country: 'PL',
                city: '',
                street: '',
                houseNumber: '',
                apartmentNumber: '',
                zipCode: '',
                commune: '',
                county: '',
                voivodeship: '',
            },
            email: '',
            phone: '',
            cellPhone: ''
        },
        buyer: {
            userId: undefined,
            name: '',
            isCompany: true,
            nip: {
                nip: ''
            },
            address: {
                country: 'PL',
                city: '',
                street: '',
                houseNumber: '',
                apartmentNumber: '',
                zipCode: '',
                commune: '',
                county: '',
                voivodeship: '',
            },
            email: '',
            phone: '',
            cellPhone: ''
        },
        currency: {
            code: 'PLN',
            exchangeRate: 1,
            exchangeRateDataUts: getCurrentDate()
        },
        payment: {
            code: 'transfer',
            deadline: 7
        },
        paymentBankAccount: undefined,
        payments: [],
        items: [],
        discounts: [],
        placeOfIssue: '',
        amountToPay: 0,
        amountPaid: 0,
        comments: '',
        bdo: '',
        signatureOfTheIssuer: '',
        signatureOfTheReceiver: '',
        splitPayment: false,
        settings: {
            sendEmail: false,
            emailToSend: '',
            isPeriodicInvoice: false,
            issuingPeriod: 'month',
            enabledAutomaticDocumentSend: false,
            automaticSendToEmails: [],
        },
        associations: []
    };
};

export const setupInvoiceData = (setInvoice, data, guid) => {
    setInvoice(draft => {
      draft.id = nanoid();
      draft.guid = guid;
      draft.series = data.series;
      if(data.series === undefined) {
        draft.seriesPrefix = DefaultSeriesPrefix;
      } else {
        draft.seriesPrefix = data.series[0];
      }

      draft.number = data.number;
      if(data.numberPromise === undefined || data.numberPromise === '') {
        draft.numberPromise = data.number;
      } else {
        draft.numberPromise = data.numberPromise;
      }

      draft.dateOfIssueUtc = dayjs(data.dateOfIssueUtc);
      draft.dateOfSaleUtc = dayjs(data.dateOfSaleUtc);
      draft.seller = {...data.seller};
      draft.buyer = data.buyer;
      draft.currency = {
          code: data.currency.code,
          exchangeRate: data.currency.exchangeRate,
          exchangeRateDataUts: dayjs(data.currency.exchangeRateDataUts)
      };

      draft.payment = {
          code: data.payment.code,
          deadline: data.payment.deadline,
      };

      draft.paymentBankAccount = data.paymentBankAccount;
      draft.items = data.items.map((item) => toItemDataGridRow(item));
      draft.discounts = data.discounts;
      draft.placeOfIssue = data.placeOfIssue;
      draft.amountToPay = data.amountToPay;
      draft.amountPaid = data.amountPaid;
      draft.comments = data.comments;
      draft.bdo = data.bdo;
      if (data.settings != null) {
        draft.settings = data.settings;
      }

      draft.signatureOfTheIssuer = data.signatureOfTheIssuer;
      draft.signatureOfTheReceiver = data.signatureOfTheReceiver;
      draft.associations = data.associations;
      draft.splitPayment = data.splitPayment;
    });
  }  

export const updateSeller = (setInvoice, setInvoiceValidation, payload) => {
    setInvoice(draft => { draft.seller = payload });
    setSellerValidationObjectValuesTo(setInvoiceValidation, true);
    setInvoiceValidation(draft => {draft.placeOfIssue = true});
  };
  
export const updateBuyer = (setInvoice, setInvoiceValidation, payload) => {
    setInvoice(draft => {
      draft.buyer = payload.data;
      if(payload.buyerId !== null) {
        draft.buyer.userId = payload.buyerId;
      }
    });
    setBuyerValidationObjectValuesTo(setInvoiceValidation, true);
  };

export const updateBuyerUserId = (setInvoice, payload) => {
      setInvoice(draft => {draft.buyer.userId = payload});
  }

export const updateInvoice = (setInvoice, setInvoiceValidation, payload) => {
    const newValue = payload.value;

    switch (payload.type) {
        case 'series': { setInvoice(draft =>{draft.series = newValue}); return; }
        case 'seriesPrefix': { setInvoice(draft => {draft.seriesPrefix = newValue}); return; }
        case 'number': {
          setInvoice(draft => {
            draft.number = newValue;
            draft.numberPromise = newValue;
          });
          return;
        }
        case 'dateOfIssueUtc': {
          setInvoice(draft => {draft.dateOfIssueUtc = newValue});
          setInvoiceValidation(draft => {draft.dateOfIssueUtc = (newValue !== "")});
          return;
        }
        case 'dateOfSaleUtc': {
          setInvoice(draft => {draft.dateOfSaleUtc = newValue});
          setInvoiceValidation(draft => {draft.dateOfSaleUtc = (newValue !== "")});
          return;
        }
        case 'currency.code': { setInvoice(draft => {draft.currency.code = newValue}); return; }
        case 'items': { setInvoice(draft => {draft.items = newValue}); return; }
        case 'paymentBankAccount': { setInvoice(draft => {draft.paymentBankAccount = newValue}); return; }
        case 'payments': { setInvoice(draft => {draft.payments = newValue}); return; }
        case 'payment.code': { setInvoice(draft => {draft.payment.code = newValue}); return; }
        case 'payment.deadline': { setInvoice(draft => {draft.payment.deadline = newValue}); return; }
        case 'placeOfIssue': {
          setInvoice(draft => {draft.placeOfIssue = newValue});
          setInvoiceValidation(draft => {draft.placeOfIssue = (newValue !== "")});
          return;
        }
        case 'amountToPay': { setInvoice(draft => {draft.amountToPay = newValue}); return; }
        case 'amountPaid': { setInvoice(draft => {draft.amountPaid = newValue}); return; }
        case 'comments': { setInvoice(draft => {draft.comments = newValue}); return; }
        case 'bdo': { setInvoice(draft => {draft.bdo = newValue}); return; }
        case 'splitPayment': { setInvoice(draft => {draft.splitPayment = newValue}); return; }
        case 'signatureOfTheIssuer': { setInvoice(draft => {draft.signatureOfTheIssuer = newValue}); return; }
        case 'signatureOfTheReceiver': { setInvoice(draft => {draft.signatureOfTheReceiver = newValue}); return; }
        case 'settings.emailToSend': { setInvoice(draft => {draft.settings.emailToSend = newValue}); return; }
        case 'settings.sendEmail': { setInvoice(draft => {draft.settings.sendEmail = newValue}); return; }
        case 'settings.isPeriodicInvoice': { setInvoice(draft => {draft.settings.isPeriodicInvoice = newValue}); return; }
        case 'settings.issuingPeriod': { setInvoice(draft => {draft.settings.issuingPeriod = newValue}); return; }
    }
};